import React, {FormEvent, useEffect, useRef, useState} from 'react';
import Autosuggest, {ChangeEvent} from 'react-autosuggest';
import {clearHistory, fetchHistory} from '../../services/search-service';
import {SvgIcon} from '../../icons/svgIcon';
import {Icons} from '../../icons/icons';
import {Button} from 'react-bootstrap';
import {SuggestionEntry, SuggestionSection} from './search-bar-types';
import {useSearchPageType, useStateHistoryEntries, useStateQueryPending} from '../search-hooks';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {getAutocompleteSuggestions} from './search-bar-service';
import {buildDefaultFieldTitle} from '../../utils/utils';

type SearchInputFieldProps = {
    readonly placeholder: string;
    // Using string object here to ensure calling setCurrentValue() even when query is the same.
    // Case: searching for 'clariant', then entering 'schülke' without pressing enter. Then switching search sources to bookmarks
    // -> the search field should automatically change to 'clariant'
    // eslint-disable-next-line @typescript-eslint/ban-types
    readonly initialValue: String;
    readonly onSubmit: (searchQuery: string) => void;
};

type AutosuggestElement = {
    input: HTMLInputElement;
};

const SUGGESTIONS_MIN_CHARS = 2;

export const SearchInputField = React.memo(({placeholder, initialValue, onSubmit}: SearchInputFieldProps) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const searchPageType = useSearchPageType();
    const searchInput = useRef<AutosuggestElement | null>(null);

    const historyEntries = useStateHistoryEntries();
    const queryPending = useStateQueryPending();
    const container = useSelector((state) => state.settings.containerName);
    const suggestFields = useSelector((state) => state.settings.containers[container]?.suggestFields);

    const [suggestions, setSuggestions] = useState<SuggestionSection[]>([]);
    const [currentValue, setCurrentValue] = useState<string>('');


    // React initially passes "" as the `initialValue`, since the redux state is not yet updated with the query from the URL
    // As soon as the redux state changes with the real query, it will change the prop `initialValue` to the query
    useEffect(() => {
        setCurrentValue(initialValue.toString() || '');
    }, [initialValue]);

    useEffect(() => {
        dispatch(fetchHistory(searchPageType));
    }, [initialValue, dispatch, searchPageType]);


    const getSectionSuggestions = (suggestionSection: SuggestionSection) => {
        return suggestionSection.entries;
    };

    const renderSuggestionsContainer = ({containerProps, children, query}) => {
        return (
            <div {...containerProps}>
                {!query.length && children && (
                    <div id="clear-history-button">
                        <Button variant="link" onClick={onClearHistory}>
                            {t('search.label.history_clear')}
                        </Button>
                    </div>
                )}
                {children}
            </div>
        );
    };

    const renderSectionTitle = (suggestionSection: SuggestionSection) => {
        return <span>{suggestionSection.title}</span>;
    };

    const getSuggestionValue = (suggestion: SuggestionEntry) => {
        return suggestion.value;
    };

    const renderSuggestion = (suggestion: SuggestionEntry) => {
        return (
            <div>
                {suggestion.description && <span className="suggest-description">{suggestion.description}</span>}
                <span className="suggest-query">{suggestion.value}</span>
            </div>
        );
    };

    const onClearHistory = () => {
        dispatch(clearHistory(searchPageType));
        setSuggestions([]);
    };


    const onChange = (event: FormEvent, changeEvent: ChangeEvent) => {
        setCurrentValue(changeEvent.newValue);
    };

    const setHistorySuggestions = () => {
        const result: SuggestionSection[] = [];
        historyEntries.forEach((entry) => {
            result.push({
                title: t('search.label.history') + ' ' + entry.date,
                entries: entry.queries.map((q) => {
                    return {
                        value: q.query,
                        description: q.time,
                    };
                }),
            });
        });
        setSuggestions(result);
    };

    const applyAutocompleteSuggestions = (suggestionsParam?: Record<string, string[]>) => {
        if (!suggestionsParam) {
            return;
        }
        setSuggestions(
            Object.entries(suggestionsParam).map((entry: [string, string[]]) => {
                return {
                    title:
                        t('search.label.autocomplete') + t('fieldTitle.' + entry[0] + '.label', buildDefaultFieldTitle(entry[0])),
                    entries: entry[1].map((it) => {
                        return {value: it};
                    }),
                };
            })
        );
    };

    const onSuggestionsFetchRequested = ({value}: { value: string }) => {
        if (value.trim().length > SUGGESTIONS_MIN_CHARS) {
            setAutocompleteSuggestions(value);
        } else if (!value.trim().length) {
            setHistorySuggestions();
        } else {
            setSuggestions([]);
        }
    };

    const onSuggestionSelected = (event, {suggestionValue}) => {
        onSubmit(suggestionValue);
    };

    const onSuggestionsClearRequested = () => {
        setSuggestions([]);
    };

    const setAutocompleteSuggestions = (value: string) => {
        getAutocompleteSuggestions(value, searchPageType, container, applyAutocompleteSuggestions, suggestFields);
    };

    const onSearchBarClear = () => {
        setCurrentValue('');
        onSubmit('');
        searchInput.current?.input.focus();
    };

    const renderIcon = (currentValue: string) => {
        if (currentValue === '') {
            return (
                <SvgIcon
                    className="search-bar-icon"
                    icon={Icons.SEARCH}
                    onClick={() => onSubmit(currentValue)}
                    alt={t('search.label.placeholder')}
                />
            );
        } else {
            return (
                <SvgIcon
                    className="search-bar-icon clear"
                    icon={Icons.X}
                    onClick={onSearchBarClear}
                    alt={t('general.clear')}
                />
            );
        }
    };

    const autosuggestProps = {
        placeholder: t(placeholder),
        value: currentValue,
        onChange: onChange,
        type: 'search',
        id: 'search-bar__input',
    };

    return (
        <form
            className={'hibu-search-bar-form'}
            onSubmit={(event) => {
                event.preventDefault();
                onSubmit(currentValue);
                dispatch(fetchHistory(searchPageType));
            }}
        >
            <Autosuggest
                multiSection={true}
                suggestions={suggestions}
                onSuggestionsFetchRequested={onSuggestionsFetchRequested}
                onSuggestionsClearRequested={onSuggestionsClearRequested}
                onSuggestionSelected={onSuggestionSelected}
                getSectionSuggestions={getSectionSuggestions}
                getSuggestionValue={getSuggestionValue}
                renderSuggestion={renderSuggestion}
                renderSectionTitle={renderSectionTitle}
                renderSuggestionsContainer={renderSuggestionsContainer}
                inputProps={autosuggestProps}
                shouldRenderSuggestions={() => !queryPending}
                ref={searchInput}
            />
            <input type={'submit'} hidden/>
            {renderIcon(currentValue)}
        </form>
    );
});
